import React, { useEffect, useState } from 'react';
import { Flag, Lock, Plus } from 'lucide-react';
import type { FacultyMember, FacultyNote, FacultyNoteDraft, NoteCategory } from '../../types/faculty';

interface FacultyNotesTabProps {
  faculty: FacultyMember;
  onSaveNote?: (draft: FacultyNoteDraft) => Promise<void> | void;
}

const sortNotes = (notes: FacultyNote[]) =>
  [...notes].sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime());

const categoryTone: Record<NoteCategory, string> = {
  General: 'bg-muted text-text-secondary',
  Performance: 'bg-success-background text-jungle',
  HR: 'bg-muted text-text-secondary',
  Other: 'bg-muted text-text-secondary',
};

export function FacultyNotesTab({ faculty, onSaveNote }: FacultyNotesTabProps) {
  const [notes, setNotes] = useState<FacultyNote[]>(sortNotes(faculty.notes));
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [newNote, setNewNote] = useState({
    content: '',
    category: 'General' as NoteCategory,
    isImportant: false,
  });

  useEffect(() => {
    setNotes(sortNotes(faculty.notes));
  }, [faculty.id, faculty.notes]);

  const handleSaveNote = async () => {
    if (!newNote.content.trim()) return;

    if (onSaveNote) {
      setIsSaving(true);
      try {
        await onSaveNote({
          content: newNote.content.trim(),
          category: newNote.category,
          isImportant: newNote.isImportant,
        });
      } finally {
        setIsSaving(false);
      }
    } else {
      const optimisticNote: FacultyNote = {
        id: `note-${Date.now()}`,
        content: newNote.content.trim(),
        author: 'Current User',
        timestamp: new Date().toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        category: newNote.category,
        isImportant: newNote.isImportant,
      };
      setNotes((current) => [optimisticNote, ...current]);
    }

    setNewNote({
      content: '',
      category: 'General',
      isImportant: false,
    });
    setIsAdding(false);
  };

  return (
    <div data-testid="faculty-live-notes" className="mx-auto max-w-3xl">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center text-sm text-text-secondary">
          <Lock className="mr-1.5 h-4 w-4" />
          Internal notes only. Not visible to faculty.
        </div>
        <button
          type="button"
          data-testid="add-note-btn"
          onClick={() => setIsAdding(true)}
          className="inline-flex items-center rounded-md border border-transparent bg-primary px-3 py-1.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Add note
        </button>
      </div>

      {isAdding ? (
        <div className="animate-in fade-in slide-in-from-top-2 mb-6 rounded-xl border border-border bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-heading text-lg font-semibold text-iron-grey">New note</h3>
          <textarea
            data-testid="note-content"
            className="mb-4 min-h-[100px] w-full rounded-md border border-border p-3 text-sm text-iron-grey focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Type your note here..."
            value={newNote.content}
            onChange={(event) => setNewNote((current) => ({ ...current, content: event.target.value }))}
          />

          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <select
                data-testid="note-category"
                className="rounded-md border border-border bg-white px-3 py-1.5 text-sm text-iron-grey focus:outline-none focus:ring-1 focus:ring-primary"
                value={newNote.category}
                onChange={(event) =>
                  setNewNote((current) => ({ ...current, category: event.target.value as NoteCategory }))
                }
              >
                <option value="General">General</option>
                <option value="Performance">Performance</option>
                <option value="HR">HR</option>
                <option value="Other">Other</option>
              </select>
              <label className="flex cursor-pointer items-center text-sm text-iron-grey">
                <input
                  data-testid="note-important"
                  type="checkbox"
                  className="mr-2 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                  checked={newNote.isImportant}
                  onChange={(event) =>
                    setNewNote((current) => ({ ...current, isImportant: event.target.checked }))
                  }
                />
                Mark as important
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setIsAdding(false)}
                className="rounded-md border border-border px-4 py-1.5 text-sm font-medium text-iron-grey transition-colors hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="save-note-btn"
                disabled={!newNote.content.trim() || isSaving}
                onClick={() => void handleSaveNote()}
                className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-primary-hover"
              >
                Save note
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div data-testid="notes-list" className="space-y-4">
        {notes.length === 0 ? (
          <div
            data-testid="notes-empty"
            className="rounded-xl border border-dashed border-border bg-white py-12 text-center text-text-secondary"
          >
            No notes yet. Add a note to keep track of important information.
          </div>
        ) : null}

        {notes.map((note) => (
          <div
            key={note.id}
            className={`rounded-xl border bg-white p-5 shadow-sm ${
              note.isImportant ? 'border-coral/50 ring-1 ring-coral/20' : 'border-border'
            }`}
          >
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <span className="font-medium text-iron-grey">{note.author}</span>
                <span className="text-sm text-text-tertiary">{note.timestamp}</span>
              </div>
              <div className="flex items-center gap-2">
                {note.isImportant ? <Flag className="h-4 w-4 text-strawberry" /> : null}
                <span className={`rounded-md px-2 py-0.5 text-xs font-medium ${categoryTone[note.category]}`}>
                  {note.category}
                </span>
              </div>
            </div>
            <p className="whitespace-pre-wrap text-sm text-iron-grey">{note.content}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
